import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import { Loader2, Lock, AlertTriangle, ArrowLeft } from 'lucide-react';

const ChangePassword = () => {
  const navigate = useNavigate();
  const { changePassword, user, forcePasswordChange } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      // For forced password changes, currentPassword can be null
      await changePassword(forcePasswordChange ? null : currentPassword, newPassword);
      toast.success('Password changed successfully!');
      // Navigate back to home
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #09090B 0%, #18181B 100%)'
      }}
    >
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-primary/5 rounded-full blur-3xl" />
      </div>

      <Card className="w-full max-w-md glass border-white/10 animate-fade-in relative z-10" data-testid="change-password-card">
        <CardHeader className="text-center space-y-4 pb-6">
          {!forcePasswordChange && (
            <Button
              variant="ghost"
              size="sm"
              className="absolute top-4 left-4 text-muted-foreground hover:text-foreground"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
          )}
          <div className="mx-auto w-16 h-16 bg-amber-500/20 rounded-2xl flex items-center justify-center">
            <Lock className="w-8 h-8 text-amber-400" />
          </div>
          <div>
            <CardTitle className="text-2xl font-bold tracking-tight">
              Change Your Password
            </CardTitle>
            <CardDescription className="text-muted-foreground mt-2">
              {forcePasswordChange 
                ? 'Your password was reset by an administrator. Please set a new password to continue.'
                : 'Update your account password'
              }
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent>
          {forcePasswordChange && (
            <div className="mb-6 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <div className="flex items-center gap-2 text-amber-400">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm font-medium">Password Reset Required</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Please choose a strong password that you haven't used before.
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {!forcePasswordChange && (
              <div className="space-y-2">
                <Label htmlFor="currentPassword" className="text-sm font-medium">
                  Current Password
                </Label>
                <Input
                  id="currentPassword"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  className="h-12 bg-secondary/50 border-border focus:border-primary transition-colors"
                  required={!forcePasswordChange}
                  data-testid="current-password-input"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="newPassword" className="text-sm font-medium">
                New Password
              </Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                className="h-12 bg-secondary/50 border-border focus:border-primary transition-colors"
                required
                minLength={6}
                data-testid="new-password-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-sm font-medium">
                Confirm New Password
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                className="h-12 bg-secondary/50 border-border focus:border-primary transition-colors"
                required
                minLength={6}
                data-testid="confirm-password-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-12 text-base font-semibold bg-primary hover:bg-primary/90 transition-all duration-200"
              disabled={loading}
              data-testid="change-password-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update Password'
              )}
            </Button>
          </form>

          <p className="text-xs text-muted-foreground text-center mt-6">
            Logged in as: {user?.email}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default ChangePassword;
